// Sentry-like error tracking utility
// In production, replace with actual Sentry SDK integration

interface ErrorEvent {
  message: string;
  stack?: string;
  context?: Record<string, unknown>;
  timestamp: string;
  level: 'error' | 'warning' | 'info';
  tags?: Record<string, string>;
  user?: {
    id?: string;
    email?: string;
  };
}

class ErrorTracker {
  private static instance: ErrorTracker;
  private isInitialized = false;
  private dsn: string | null = null;
  private environment: string = 'development';
  private events: ErrorEvent[] = [];

  private constructor() {}

  static getInstance(): ErrorTracker {
    if (!ErrorTracker.instance) {
      ErrorTracker.instance = new ErrorTracker();
    }
    return ErrorTracker.instance;
  }

  init(config: { dsn?: string; environment?: string }) {
    this.dsn = config.dsn || null;
    this.environment = config.environment || 'development';
    this.isInitialized = true;

    // Set up global error handlers
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        this.captureError(event.error || new Error(event.message), {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
      });

      window.addEventListener('unhandledrejection', (event) => {
        this.captureError(
          event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
          { type: 'unhandledrejection' }
        );
      });
    }

    console.log('[ErrorTracker] Initialized', { environment: this.environment });
  }

  captureError(error: Error, context?: Record<string, unknown>) {
    const event: ErrorEvent = {
      message: error.message,
      stack: error.stack,
      context,
      timestamp: new Date().toISOString(),
      level: 'error',
      tags: {
        environment: this.environment,
      },
    };

    this.events.push(event);

    // Log to console in development
    if (this.environment === 'development') {
      console.error('[ErrorTracker] Error captured:', event);
    }

    // In production with DSN, send to error tracking service
    if (this.dsn && this.environment === 'production') {
      this.sendToService(event);
    }
  }

  captureMessage(message: string, level: 'error' | 'warning' | 'info' = 'info', context?: Record<string, unknown>) {
    const event: ErrorEvent = {
      message,
      context,
      timestamp: new Date().toISOString(),
      level,
      tags: {
        environment: this.environment,
      },
    };

    this.events.push(event);

    if (this.environment === 'development') {
      console.log(`[ErrorTracker] ${level}:`, message, context);
    }

    if (this.dsn && this.environment === 'production') {
      this.sendToService(event);
    }
  }

  setUser(user: { id?: string; email?: string }) {
    // Store user context for future events
    console.log('[ErrorTracker] User set:', user.id);
  }

  private async sendToService(event: ErrorEvent) {
    // Placeholder for actual Sentry/error service integration
    // Replace with actual Sentry SDK when DSN is configured:
    // Sentry.captureException(error) or Sentry.captureMessage(message)
    
    try {
      // For now, just log that we would send
      console.log('[ErrorTracker] Would send to service:', event.message);
    } catch (e) {
      console.error('[ErrorTracker] Failed to send event:', e);
    }
  }

  getEvents(): ErrorEvent[] {
    return [...this.events];
  }

  clearEvents() {
    this.events = [];
  }
}

export const errorTracker = ErrorTracker.getInstance();

// Initialize with environment detection
export function initErrorTracking() {
  const isProd = import.meta.env.PROD;
  
  errorTracker.init({
    // Add your Sentry DSN here when you have one:
    // dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: isProd ? 'production' : 'development',
  });
}
